import React, { useState } from 'react';
import { Avatar, MessageSeparator, Message, MessageList } from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { Box, Typography, Divider, Button } from '@mui/material';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MessageFooter from 'components/structure/AIChatAssistant/MessageFooter';
import './ChatCard.css';
import {
  AI_CHAT_ASSISTANT,
  AIAssistantMessageDirection,
  AIAssistantMessagePosition,
  CHAT_USER,
  colours
} from 'utils/constants';
import { parseConversation, transformRawMessage } from 'utils/parse';
import { AIAssistantMessageType, ParsedConversation } from 'services/hooks/ai-assistant-service';

interface ChatCardProps {
  data: any;
  isFeedback: boolean;
}

function getLastThreePairs(messages: AIAssistantMessageType[]): AIAssistantMessageType[] {
  const pairs: AIAssistantMessageType[] = [];
  let i = messages.length - 1;
  while (i >= 0 && pairs.length < 6) {
    pairs.unshift(messages[i]);
    i--;
  }
  return pairs;
}

const ChatCard: React.FC<ChatCardProps> = ({ data, isFeedback }) => {
  const date = new Date(data.timestamp || data.updated_at).toLocaleDateString();

  let messages: AIAssistantMessageType[] = [];

  if (isFeedback) {
    // For Feedbacks tab
    messages = [
      {
        message: data.prompt,
        sender: CHAT_USER,
        direction: AIAssistantMessageDirection.Outgoing
      },
      {
        message: data.answer,
        sender: AI_CHAT_ASSISTANT,
        direction: AIAssistantMessageDirection.Incoming
      }
    ];
  } else {
    // For Conversations tab
    if (data.conversation) {
      const rawMessages: ParsedConversation | null = parseConversation(data.conversation) || [];
      const allMessages: AIAssistantMessageType[] = rawMessages.map((rawMsg) =>
        transformRawMessage(rawMsg, AIAssistantMessagePosition.Normal)
      );
      messages = getLastThreePairs(allMessages);
    }
  }

  let lastDate: string | null = null;

  const initialFeedbackState: 'liked' | 'disliked' | null =
    data.isGood === true ? 'liked' : data.isGood === false ? 'disliked' : null;

  const feedbackState: { [key: number]: 'liked' | 'disliked' | null } = {
    1: initialFeedbackState
  };

  const [showAll, setShowAll] = useState<boolean>(false);

  const hasMoreMessages =
    !isFeedback && data.conversation
      ? (parseConversation(data.conversation) || []).length > 6
      : false;

  const toggleShow = () => {
    setShowAll((prev) => !prev);
  };

  const displayedMessages = isFeedback
    ? messages
    : showAll
    ? (parseConversation(data.conversation) || []).map((rawMsg) =>
        transformRawMessage(rawMsg, AIAssistantMessagePosition.Normal)
      )
    : messages;

  return (
    <Box
      className="custom-chat-card"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
        p: 2,
        boxShadow: 2
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="body2" color="textSecondary">
          {date}
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Typography variant="body2" color="textSecondary">
          {data.username}
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Typography variant="body2" color="textSecondary">
          {data.platform}
        </Typography>
      </Box>

      {!isFeedback && hasMoreMessages && (
        <Box display="flex" justifyContent="center" mb={2}>
          <Button
            variant="text"
            onClick={toggleShow}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              color: colours.seafoam,
              '&:hover': {
                backgroundColor: '#e3f2fd'
              },
              padding: '6px 12px',
              borderRadius: '8px'
            }}
          >
            {showAll ? 'Show less' : 'Show more'}
          </Button>
        </Box>
      )}

      {/* Chat Messages */}
      <MessageList className="message-list-container">
        {displayedMessages.map((msg, index) => {
          // Extract date and time from message's timestamp
          const messageDate = msg.timestamp
            ? new Date(msg.timestamp).toLocaleDateString('en-US', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })
            : null;

          const messageTime = msg.timestamp
            ? new Date(msg.timestamp).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })
            : null;

          const shouldAddSeparator = messageDate !== lastDate;

          lastDate = messageDate || lastDate;

          return (
            <React.Fragment key={index}>
              {shouldAddSeparator && (
                <MessageSeparator className="message-separator">
                  {messageDate || ''}
                </MessageSeparator>
              )}

              <Message
                key={index}
                model={{
                  sender: msg.sender,
                  direction: msg.direction,
                  position: msg.position || AIAssistantMessagePosition.Normal,
                  type: 'custom'
                }}
                style={{ width: '100%' }}
                avatarPosition="tl"
                avatarSpacer
                className={`chat-message ${
                  msg.direction === AIAssistantMessageDirection.Outgoing
                    ? CHAT_USER
                    : AI_CHAT_ASSISTANT
                }`}
              >
                {msg.sender === AI_CHAT_ASSISTANT && <Avatar size="sm" src="/favicon.ico" />}
                <Message.CustomContent className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {DOMPurify.sanitize(msg.message)}
                  </ReactMarkdown>
                </Message.CustomContent>

                {/* For Feedbacks tab, show the MessageFooter */}
                {msg.sender === AI_CHAT_ASSISTANT && isFeedback && (
                  <Message.Footer className="message-footer">
                    <MessageFooter
                      message={msg}
                      index={index}
                      messages={messages}
                      feedbackState={feedbackState}
                      handleThumbsUp={() => {}}
                      handleThumbsDown={() => {}}
                      setSnackbar={() => {}}
                      setFeedbackUuid={() => {}}
                      handleOpenFeedbackModal={() => {}}
                      disableThumbsUp={true}
                      disableThumbsDown={true}
                    />
                  </Message.Footer>
                )}

                {/* Show the time footer for Conversations tab */}
                {!isFeedback && messageTime && (
                  <Message.Footer className="message-footer-time">
                    <Typography variant="caption" color="textSecondary">
                      {messageTime}
                    </Typography>
                  </Message.Footer>
                )}
              </Message>
            </React.Fragment>
          );
        })}
      </MessageList>

      {/* Feedback Section for Feedbacks tab */}
      {isFeedback && (
        <Box mt={1}>
          <Typography variant="subtitle2">Feedback:</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {data.feedback || 'No feedback provided'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChatCard;
